import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { Pencil, Trash2, Plus, ArrowLeft } from "lucide-react";

const CATEGORIES = [
  "The Trees of Scripture",
  "The Practice",
  "From the Woods",
  "Creation & Stillness",
  "The Companion Tree",
];

const EMPTY = { title: "", category: CATEGORIES[0], teaser: "", body: "", cover: "", featured: false };

const inputCls =
  "w-full rounded-md border border-cream/20 bg-navy-deep/60 px-4 py-2.5 text-sm text-cream placeholder:text-cream/35 focus:border-amber focus:outline-none transition-colors duration-300";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("tc_admin_token", data.token);
      onLogin(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-svh items-center justify-center bg-navy px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-md border border-cream/10 bg-navy-deep/60 p-8"
      >
        <img src="/images/logo.png" alt="" className="mx-auto h-14 w-14" />
        <h1 className="mt-4 text-center font-playfair text-2xl text-cream">The Companion Tree — Admin</h1>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          data-testid="admin-email-input"
          className={`${inputCls} mt-8`}
        />
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          data-testid="admin-password-input"
          className={`${inputCls} mt-3`}
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-3 text-sm text-amber">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-5 w-full rounded-full bg-amber py-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-navy-deep transition-colors duration-300 hover:bg-cream disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <Link
          to="/"
          data-testid="admin-back-home"
          className="mt-5 block text-center text-[11px] uppercase tracking-[0.2em] text-cream/50 hover:text-cream"
        >
          Back to the site
        </Link>
      </form>
    </div>
  );
}

function EssayForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [covers, setCovers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/covers").then((r) => setCovers(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="rounded-md border border-cream/10 bg-navy-deep/60 p-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input
          required
          value={form.title}
          onChange={set("title")}
          placeholder="Reflection title"
          data-testid="essay-form-title"
          className={inputCls}
        />
        <select value={form.category} onChange={set("category")} data-testid="essay-form-category" className={inputCls}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c} className="bg-navy-deep">
              {c}
            </option>
          ))}
        </select>
      </div>
      <input
        value={form.teaser}
        onChange={set("teaser")}
        placeholder="One-line teaser"
        data-testid="essay-form-teaser"
        className={`${inputCls} mt-4`}
      />
      <select value={form.cover} onChange={set("cover")} data-testid="essay-form-cover" className={`${inputCls} mt-4`}>
        <option value="" className="bg-navy-deep">
          No cover image
        </option>
        {covers.map((c) => (
          <option key={c} value={c} className="bg-navy-deep">
            {c.split("/").pop()}
          </option>
        ))}
      </select>
      {form.cover && (
        <img src={form.cover} alt="Cover preview" className="mt-3 h-32 rounded-md object-cover" />
      )}
      <textarea
        value={form.body}
        onChange={set("body")}
        placeholder="Reflection body — separate paragraphs with a blank line"
        rows={10}
        data-testid="essay-form-body"
        className={`${inputCls} mt-4 font-mono`}
      />
      <label className="mt-4 flex items-center gap-3 text-sm text-cream/80">
        <input
          type="checkbox"
          checked={form.featured}
          onChange={set("featured")}
          data-testid="essay-form-featured"
          className="h-4 w-4 accent-[#C68326]"
        />
        Feature this reflection at the top of The Companion Tree
      </label>
      <div className="mt-6 flex gap-3">
        <button
          type="submit"
          disabled={saving}
          data-testid="essay-form-save"
          className="rounded-full bg-amber px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy-deep transition-colors duration-300 hover:bg-cream disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save reflection"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="essay-form-cancel"
          className="rounded-full border border-cream/25 px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-cream/70 transition-colors duration-300 hover:border-cream"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("tc_admin_token"));
  const [tab, setTab] = useState("essays");
  const [essays, setEssays] = useState([]);
  const [subs, setSubs] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, "new" = create, essay obj = edit

  const logout = () => {
    localStorage.removeItem("tc_admin_token");
    setToken(null);
  };

  const loadEssays = () => api.get("/admin/essays").then((r) => setEssays(r.data)).catch((e) => {
    if (e.response?.status === 401) logout();
  });
  const loadSubs = () => api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {});

  useEffect(() => {
    if (token) {
      loadEssays();
      loadSubs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) return <LoginForm onLogin={setToken} />;

  const saveEssay = async (form) => {
    if (editing && editing !== "new") {
      await api.put(`/admin/essays/${editing.id}`, form);
    } else {
      await api.post("/admin/essays", form);
    }
    setEditing(null);
    loadEssays();
  };

  const deleteEssay = async (id) => {
    if (!window.confirm("Delete this reflection? This cannot be undone.")) return;
    await api.delete(`/admin/essays/${id}`);
    loadEssays();
  };

  const exportCsv = async () => {
    const r = await api.get("/admin/subscribers/export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "companion-tree-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-svh bg-navy px-6 py-24 sm:px-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-playfair text-3xl text-cream">The Companion Tree — Admin</h1>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-cream/60 hover:text-cream">
              <ArrowLeft size={13} /> View site
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="text-[11px] uppercase tracking-[0.2em] text-cream/60 hover:text-amber">
              Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-2">
          {["essays", "subscribers"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`rounded-full border px-5 py-2 text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === t ? "border-amber bg-amber/15 text-amber" : "border-cream/20 text-cream/60 hover:text-cream"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "essays" && (
          <div className="mt-8">
            {editing === null ? (
              <>
                <button
                  onClick={() => setEditing("new")}
                  data-testid="admin-new-essay"
                  className="inline-flex items-center gap-2 rounded-full bg-amber px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy-deep transition-colors duration-300 hover:bg-cream"
                >
                  <Plus size={14} /> New reflection
                </button>
                <div className="mt-6 divide-y divide-cream/10 border-y border-cream/10">
                  {essays.length === 0 && (
                    <p className="py-10 text-sm text-cream/50">No reflections yet — write the first one.</p>
                  )}
                  {essays.map((e, i) => (
                    <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`admin-essay-row-${i}`}>
                      <div>
                        <p className="font-playfair text-lg text-cream">
                          {e.title} {e.featured && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-amber">Featured</span>}
                        </p>
                        <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-cream/40">
                          {e.category} · {new Date(e.created_at).toLocaleDateString("en-US")}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditing(e)}
                          data-testid={`admin-edit-essay-${i}`}
                          className="rounded-full border border-cream/20 p-2.5 text-cream/70 transition-colors duration-300 hover:border-amber hover:text-amber"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => deleteEssay(e.id)}
                          data-testid={`admin-delete-essay-${i}`}
                          className="rounded-full border border-cream/20 p-2.5 text-cream/70 transition-colors duration-300 hover:border-red-400 hover:text-red-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <EssayForm
                initial={editing === "new" ? EMPTY : editing}
                onSave={saveEssay}
                onCancel={() => setEditing(null)}
              />
            )}
          </div>
        )}

        {tab === "subscribers" && (
          <div className="mt-8">
            <button
              onClick={exportCsv}
              data-testid="admin-export-subscribers"
              className="rounded-full border border-cream/25 px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-amber hover:text-amber"
            >
              Export CSV ({subs.length})
            </button>
            <div className="mt-6 divide-y divide-cream/10 border-y border-cream/10">
              {subs.length === 0 && <p className="py-10 text-sm text-cream/50">No subscribers yet.</p>}
              {subs.map((s, i) => (
                <div key={i} className="flex flex-wrap justify-between gap-2 py-3 text-sm" data-testid={`admin-subscriber-row-${i}`}>
                  <span className="text-cream">{s.email}</span>
                  <span className="text-cream/40">
                    {s.source} · {new Date(s.created_at).toLocaleDateString("en-US")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
